import React from 'react';
import { WEBSHOP_MAX_WIDTH_PX, WEBSHOP_PX_FIELD_WIDTH } from '../../_webshopUi';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, InputAdornment, Slider, Switch, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Size" card from the Header/Logo modal — controls the header band height and,
 * via the SHARED global-widths state, its max content width + background
 * stretching (the SHOP_MAX_WIDTH_HEADER(_FW_BG) settings, same as
 * GlobalWidthSettingsModal — so both modals stay in sync and actually persist).
 *
 * value shape:           { logoBarHeight: number }               // px → HEADER_HEIGHT
 * globalHeaderWidth:     { width: number, bgFullWidth: bool }    // shared global widths
 * onChangeGlobalHeaderWidth(patch)  — partial, e.g. { width: 1500 }
 */
const HeaderSizeCard = ({ value = {}, onChange, globalHeaderWidth, onChangeGlobalHeaderWidth, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);
    // Width + background stretch flow to the shared global-widths state (like
    // FooterWidthCard) so they persist to SHOP_MAX_WIDTH_HEADER(_FW_BG).
    const hWidth       = Number(globalHeaderWidth?.width) || 1500;
    const hBgFullWidth = globalHeaderWidth?.bgFullWidth !== false;
    const emitW = (patch) => onChangeGlobalHeaderWidth && onChangeGlobalHeaderWidth(patch);

    return (
        <ConfigCard
            anchor="cfg-card-header-size"
            title={tr('cfg_header_size_title', 'Size')}
            description={tr('cfg_header_size_desc', 'How tall and wide the logo bar should be.')}
            theme={theme}
        >
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_header_size_logo_height', 'Logo bar height')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={40}
                        max={300}
                        step={5}
                        value={Number(get('logoBarHeight', 100)) || 100}
                        onChange={(_e, v) => set('logoBarHeight')(v)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={get('logoBarHeight', 100) ?? 100}
                        onChange={(e) => set('logoBarHeight')(Math.max(0, parseInt(e.target.value) || 0))}
                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        sx={{ width: WEBSHOP_PX_FIELD_WIDTH }}
                    />
                </Box>
            </Box>

            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_header_size_max_width', 'Maximum content width')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={400}
                        max={WEBSHOP_MAX_WIDTH_PX}
                        step={20}
                        value={hWidth}
                        onChange={(_e, v) => emitW({ width: v })}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={hWidth}
                        onChange={(e) => emitW({ width: Math.max(0, parseInt(e.target.value) || 0) })}
                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        sx={{ width: WEBSHOP_PX_FIELD_WIDTH }}
                    />
                </Box>
            </Box>

            <FormControlLabel
                control={
                    <Switch color="success"
                        size="small"
                        checked={hBgFullWidth}
                        onChange={(e) => emitW({ bgFullWidth: e.target.checked })}
                    />
                }
                label={<Typography variant="body2">{tr('cfg_header_size_bg_full', 'Background stretches to viewport edges')}</Typography>}
            />
        </ConfigCard>
    );
};

HeaderSizeCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    globalHeaderWidth: PropTypes.shape({
        width:       PropTypes.number,
        bgFullWidth: PropTypes.bool,
    }),
    onChangeGlobalHeaderWidth: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default HeaderSizeCard;
